import useI18n from "@/hooks/useI18n";
import { DrawerItem, ListGroup } from "@gisce/oficina-virtual-components"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom";
import Settings from "../../../settings";
import { useSelector } from "react-redux";
import {
  Person,
  PersonAdd,
  Message,
  MapsHomeWork,
  Receipt,
  Equalizer,
  Apps
} from "@mui/icons-material";
import { RootState } from "@/store";
import { useNavigationItemsContext } from "../useNavigationItemsContext";
import useRedirectActions from "@/hooks/useRedirectActions";
import ContractationListItems from "./CustomItems/ContractationListItems";
import useContractation from "@/components/Contractation/hooks/useContractation";
import useAuth from "@/hooks/useAuth";
import { useMediaQuery, useTheme } from "@mui/material";

export const GroupedSideBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { currentPath } = useNavigationItemsContext();
  const { isContractationPath } = useContractation();

  const { tokenVals } = useAuth();

  const theme = useTheme();
  const breakpointDownMd = useMediaQuery(theme.breakpoints.down('md'));

  const { 
    isAuthenticated, redirectAction, redirectActionExecuted
  } = useSelector((state: RootState) => state.auth);

  const { 
    beginContractAction, beginInvoiceAction 
  } = useRedirectActions({isAuthenticated});

  const ContractListGroup = <ListGroup
    icon={<MapsHomeWork />}
    titleLabel={t('common:text.home_contract_title')}
    startOpen={
      ( location.pathname === t('common:url.contracts') 
        || currentPath == 'common:url.contracts_detail')
        || isContractationPath(currentPath)
        && ( redirectAction != "consumption" || redirectActionExecuted )}
  >
    {<ContractationListItems />}
    <DrawerItem
      label={t('common:text.home_contract_list_title')}
      onClick={() => beginContractAction("view", false)}
      selected={
        ( location.pathname === t('common:url.contracts') 
          || currentPath == 'common:url.contracts_detail' )
          && ( redirectAction == "view" || !redirectAction || redirectActionExecuted )
        }
    />
    <DrawerItem
      label={t('common:text.home_contract_download_title')}
      onClick={() => beginContractAction("download")}
      selected={location.pathname === t('common:url.contracts') && redirectAction == "download"}
    />
    {Settings.features.powerChange && (
      <DrawerItem
        toolTipProps={{
          title: tokenVals?.readonly ?
            t("common:text.token_readonly_tooltip")
          :
            "",
          placement: breakpointDownMd ? "bottom" : "right"
        }}
        label={t('common:text.home_contract_power_change_title')}
        disabled={tokenVals?.readonly}
        showTooltip={tokenVals?.readonly}
        onClick={() => beginContractAction("powChange")}
        selected={location.pathname === t('common:url.contracts') && redirectAction == "powChange"}
      />
    )}
    {Settings?.contract?.ibanChange?.enabled && (
      <DrawerItem
        toolTipProps={{
          title: tokenVals?.readonly ?
            t("common:text.token_readonly_tooltip")
          :
            "",
          placement: breakpointDownMd ? "bottom" : "right"
        }}
        label={t('common:text.home_contract_bank_change_title')}
        disabled={tokenVals?.readonly}
        showTooltip={tokenVals?.readonly}
        onClick={() => beginContractAction("ibanChange")}
        selected={location.pathname === t('common:url.contracts') && redirectAction == "ibanChange"}
      />
    )}
    {Settings.features.notificacionAddressChange && (
      <DrawerItem
        toolTipProps={{
          title: tokenVals?.readonly ?
            t("common:text.token_readonly_tooltip")
          :
            "",
          placement: breakpointDownMd ? "bottom" : "right"
        }}
        label={t('common:text.home_contract_contact_change_title')}
        disabled={tokenVals?.readonly}
        showTooltip={tokenVals?.readonly}
        onClick={() => beginContractAction("notifAddrChange")}
        selected={location.pathname === t('common:url.contracts') && redirectAction == "notifAddrChange"}
      />
    )}
    {Settings.features.noCesionSips && (
      <DrawerItem
        toolTipProps={{
          title: tokenVals?.readonly ?
            t("common:text.token_readonly_tooltip")
          :
            "",
          placement: breakpointDownMd ? "bottom" : "right"
        }}
        label={t('common:text.home_contract_sips_change_title')}
        disabled={tokenVals?.readonly}
        showTooltip={tokenVals?.readonly}
        onClick={() => beginContractAction("noCesionSips")}
        selected={location.pathname === t('common:url.contracts') && redirectAction == "noCesionSips"}
      />
    )}
  </ListGroup>

  const InvoicesListGroup = <ListGroup 
    icon={<Receipt />}
    titleLabel={t('common:text.home_invoices_title')}
    startOpen={[t('common:url.invoices'), t('common:url.contracts_invoices', {contractName: params.contractName})].includes(location.pathname)}
  >
    <DrawerItem
      label={t('common:text.home_invoices_list_title')}
      onClick={() => beginInvoiceAction("view")}
      selected={
        (location.pathname === t('common:url.invoices') && (redirectAction == "view" || !redirectAction)) 
        || (location.pathname === t('common:url.contracts_invoices', {contractName: params.contractName}))
      }
    />
    <DrawerItem
      label={t('common:text.home_invoices_download_title')}
      onClick={() => beginInvoiceAction("download")}
      selected={location.pathname === t('common:url.invoices') && redirectAction == "download"}
    />
    {Settings?.invoices?.enablePayment && (
      <DrawerItem
        label={t('common:text.home_invoices_online_pay_title')}
        onClick={() => beginInvoiceAction("payOnline")}
        selected={location.pathname === t('common:url.invoices') && redirectAction == "payOnline"}
      />
    )}
  </ListGroup>

  const ConsumptionListGroup = <ListGroup
    icon={<Equalizer />}
    titleLabel={t('common:text.home_consumption_title')}
    startOpen={location.pathname === t('common:url.contracts') && redirectAction == "consumption"}
  >
	  <DrawerItem
      label={t('common:text.home_consumption_view_title')}
      onClick={() => beginContractAction("consumption")}
      selected={location.pathname === t('common:url.contracts') && redirectAction == "consumption"}
    />
  </ListGroup>

  return <>
    <DrawerItem
      icon={<Apps />}
      label={t('common:text.home')}
      onClick={() => navigate('/')}
      selected={location.pathname === '/'}
    />

    {ContractListGroup}
    {InvoicesListGroup}
    {ConsumptionListGroup}

    {!isAuthenticated && <>
      {!Settings.features?.oidc && <DrawerItem
        icon={<Person />}
        label={t('common:text.login_title')}
        onClick={() => navigate(t('common:url.login'))}
        selected={location.pathname === t('common:url.login')}
      />}
      {Settings.features?.signup && <DrawerItem
        icon={<PersonAdd />}
        label={t('common:text.login_view_button_activate')}
        onClick={() => navigate(t('common:url.activate'))}
        selected={location.pathname === t('common:url.activate')}
      />}
      {Settings.features?.sidebarContract &&
        <ContractationListItems
      />}
      {Settings.features?.unsignedContact && 
        Settings.features?.contact && <DrawerItem
        icon={<Message />}
        label={t('common:text.contact_title')}
        onClick={() => navigate(t('common:url.unsigned_contact'))}
        selected={location.pathname === t('common:url.unsigned_contact')}
      />}
    </>}
  </>
}
